
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Component, OnInit, Input } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { merge, map, filter } from 'rxjs';
import { COMPONENT_ID_HEADER } from '../../core/consts/api.const';
import { SimulatorType } from '../../core/enums/simulator-type.enum';
import { LoadingService } from '../../services/loading.service';
import { SimulationsService } from '../../services/simulations/simulations.service';
import { SnackbarService } from '../../services/snackbar.service';
import { Destroyable } from '../../utils/mixins/destroyable.mixin';
import { SimulatorConfig } from '../../core/models/simulation.model';
import { SelectRockFilePopupComponent } from '../popups/select-rock-file-popup/select-rock-file-popup.component';
import { sanitizeFileName, sanitizeSimulationName } from '../../utils/sanitize.util';
import { MatDialog } from '@angular/material/dialog';
import { RockFile } from '../../core/models/rock-files.model';
import { RockThumbnailService } from '../../services/rock-thumbnail/rock-thumbnail.service';
import { RockImagesService } from '../../services/rock-images/rock-images.service';
import { RockFileLoader } from '../../utils/rock-file-loader.util';
import { RockPreviewComponent } from '../rock-preview/rock-preview.component';
import { RemoveExtensionPipe } from '../pipes/remove-extension.pipe';

@Component({
  selector: 'app-cube-trend-simulator',
  standalone: true,
  imports: [
    FormsModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    RockPreviewComponent,
    RemoveExtensionPipe
],
  templateUrl: './cube-trend-simulator.component.html',
  styleUrl: './cube-trend-simulator.component.scss',
})
export class CubeTrendSimulatorComponent extends Destroyable(Object) implements OnInit {
  @Input() config!: SimulatorConfig;

  simulationDirectionsForm!: FormGroup;
  simulationNameForm!: FormGroup;
  cubeTrendSizesForm!: FormGroup;
  fileName: string = '';
  isLoading: boolean = false;
  thumbnailUrl: string | null = null;
  rockFileMetadata: RockFile | null = null;
  
  private rockFileLoader: RockFileLoader;

  get componentId(): SimulatorType {
    return this.config.simulatorType;
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly simulationsService: SimulationsService,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService,
    private readonly loadingService: LoadingService,
    private readonly rockThumbnailService: RockThumbnailService,
    private readonly rockImagesService: RockImagesService,
  ) {
    super();
    this.rockFileLoader = new RockFileLoader(rockThumbnailService, rockImagesService);
  }

  get totalPorosity(): string {
    const fractionHistogram = this.rockFileMetadata?.fractionHistogram;
    if (fractionHistogram && fractionHistogram['0'] != null) {
      const porosity = fractionHistogram['0'];
      return porosity.toFixed(2);
    }
    return 'N/A';
  }

  get cubeTrendSize1Control(): FormControl {
    return this.cubeTrendSizesForm.get('cubeTrendSize1') as FormControl;
  }

  get cubeTrendSize2Control(): FormControl {
    return this.cubeTrendSizesForm.get('cubeTrendSize2') as FormControl;
  }

  get cubeTrendSize3Control(): FormControl {
    return this.cubeTrendSizesForm.get('cubeTrendSize3') as FormControl;
  }

  get simulateCubeTrendSize1Control(): FormControl {
    return this.cubeTrendSizesForm.get('simulateCubeTrendSize1') as FormControl;
  }

  get simulateCubeTrendSize2Control(): FormControl {
    return this.cubeTrendSizesForm.get('simulateCubeTrendSize2') as FormControl;
  }

  get simulateCubeTrendSize3Control(): FormControl {
    return this.cubeTrendSizesForm.get('simulateCubeTrendSize3') as FormControl;
  }

  get nameControl(): FormControl {
    return this.simulationNameForm.get('name') as FormControl;
  }

  ngOnInit(): void {
    this.initSimulationDirectionsForm();
    this.initSimulationNameForm();
    this.initCubeTrendSizesForm();
    this.subscribeToLoadingState();
  }

  selectRockFileName(): void {
    const dialogRef = this.dialog.open(SelectRockFilePopupComponent, {
      panelClass: 'large-popup-size',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean), this.takeUntilDestroyed())
      .subscribe((file: RockFile) => {
        this.fileName = file.fileName;
        this.loadThumbnail(file.fileName);
      });
  }

  private loadThumbnail(fileName: string): void {
    const previousThumbnailUrl = this.thumbnailUrl;

    // Load thumbnail (always works)
    this.rockFileLoader
      .getThumbnail(fileName)
      .pipe(this.takeUntilDestroyed())
      .subscribe((url) => {
        if (url) {
          if (previousThumbnailUrl) {
            this.rockFileLoader.revokeThumbnailUrl(previousThumbnailUrl);
          }
          this.thumbnailUrl = url;
        }
      });

    this.rockFileLoader
      .getMetadata(fileName)
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (metadata) => {
          this.rockFileMetadata = metadata;
        },
        error: () => {
          // Metadata not available, porosity will show N/A
          this.rockFileMetadata = null;
        }
      });
  }

  override ngOnDestroy(): void {
    if (this.thumbnailUrl) {
      this.rockFileLoader.revokeThumbnailUrl(this.thumbnailUrl);
    }
    super.ngOnDestroy();
  }

  runSimulation(): void {
    if (this.isSimulationInputValid()) {
      const directions = this.simulationDirectionsForm.getRawValue();

      this.loadingService.startLoading(this.componentId);

      const options = {
        headers: new HttpHeaders({
          [COMPONENT_ID_HEADER]: this.componentId,
        }),
        params: this.generateParams(),
      };

      this.simulationsService
        .addSimulation(directions, sanitizeFileName(this.fileName), options, this.componentId)
        .pipe(this.takeUntilDestroyed())
        .subscribe(() => {
          this.snackbarService.openCustom({
            message: 'Simulation started successfully',
            title: 'Success',
            type: 'success',
            action: 'Close',
          });
          this.fileName = '';

          this.router.navigate(['/dashboard']);
        });
    }
  }

  isSimulationInputValid(): boolean {
    return Boolean(
      this.fileName &&
        this.simulationNameForm.valid &&
        this.cubeTrendSizesForm.valid &&
        this.directionSelected() &&
        this.cubeSizeSelected(),
    );
  }

  private generateParams(): HttpParams {
    let httpParams: HttpParams = new HttpParams().set('name', sanitizeSimulationName(this.getSimulationName()));

    if (this.simulateCubeTrendSize1Control.value) {
      httpParams = httpParams.set('cubeTrendSize1', this.cubeTrendSize1Control.value);
    }

    if (this.simulateCubeTrendSize2Control.value) {
      httpParams = httpParams.set('cubeTrendSize2', this.cubeTrendSize2Control.value);
    }

    if (this.simulateCubeTrendSize3Control.value) {
      httpParams = httpParams.set('cubeTrendSize3', this.cubeTrendSize3Control.value);
    }

    return httpParams;
  }

  private initSimulationDirectionsForm(): void {
    this.simulationDirectionsForm = this.fb.group({
      xDirection: [true],
      yDirection: [true],
      zDirection: [true],
    });
  }

  private initSimulationNameForm(): void {
    this.simulationNameForm = this.fb.group({
      name: ['', Validators.maxLength(256)],
    });
  }

  private getSimulationName(): string {
    const { name } = this.simulationNameForm.getRawValue();
    return name || (this.fileName ?? '').split('.')[0];
  }

  private initCubeTrendSizesForm(): void {
    this.cubeTrendSizesForm = this.fb.group({
      cubeTrendSize1: [30, [Validators.min(0), Validators.max(100)]],
      cubeTrendSize2: [50, [Validators.min(0), Validators.max(100)]],
      cubeTrendSize3: [70, [Validators.min(0), Validators.max(100)]],
      simulateCubeTrendSize1: [false],
      simulateCubeTrendSize2: [false],
      simulateCubeTrendSize3: [false],
    });

    merge(
      this.cubeTrendSize1Control.valueChanges.pipe(
        map((value) => ({ value: value, formControlName: 'cubeTrendSize1' })),
      ),
      this.cubeTrendSize2Control.valueChanges.pipe(
        map((value) => ({ value: value, formControlName: 'cubeTrendSize2' })),
      ),
      this.cubeTrendSize3Control.valueChanges.pipe(
        map((value) => ({ value: value, formControlName: 'cubeTrendSize3' })),
      ),
    )
      .pipe(this.takeUntilDestroyed())
      .subscribe(({ value, formControlName }) =>
        this.enableDisableCheckboxControl(value, formControlName),
      );
  }

  private enableDisableCheckboxControl(value: number, formControlName: string): void {
    let checkBoxControl: FormControl | null = null;

    switch (formControlName) {
      case 'cubeTrendSize1':
        checkBoxControl = this.simulateCubeTrendSize1Control;
        break;
      case 'cubeTrendSize2':
        checkBoxControl = this.simulateCubeTrendSize2Control;
        break;
      case 'cubeTrendSize3':
        checkBoxControl = this.simulateCubeTrendSize3Control;
        break;
    }

    if (!checkBoxControl) return;

    if (value || value === 0) {
      checkBoxControl.enable();
    } else {
      checkBoxControl.patchValue(false);
      checkBoxControl.disable();
    }
  }

  private directionSelected(): boolean {
    const selectedDirections = this.simulationDirectionsForm.getRawValue();
    return Object.values(selectedDirections).some(Boolean);
  }

  private cubeSizeSelected(): boolean {
    return Boolean(
      this.simulateCubeTrendSize1Control.value ||
        this.simulateCubeTrendSize2Control.value ||
        this.simulateCubeTrendSize3Control.value,
    );
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((isLoading: boolean) => {
        this.isLoading = isLoading;
      });
  }
}
